import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { GearItemWithPacking } from '@/types/packing';

// Register fonts
Font.register({
  family: "Brakle",
  fonts: [
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-regular-webfont.woff",
      fontWeight: 400,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-medium-webfont.woff",
      fontWeight: 500,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-semibold-webfont.woff",
      fontWeight: 600,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-bold-webfont.woff",
      fontWeight: 700,
    },
  ],
});

interface PackingListPDFProps {
  items: GearItemWithPacking[];
  cases: any[];
  projectName: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
    fontSize: 10,
  },
  // Top bar with date/time
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  printedDate: {
    fontSize: 8,
    color: "#8E9196",
    textAlign: "right",
  },

  // Header section
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  leftColumn: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderStyle: "solid",
    borderRadius: 4,
    padding: 8,
  },

  // Project title and description
  projectTitle: {
    fontFamily: "Brakle",
    fontWeight: 700,
    fontSize: 14,
    color: "#1A1F2C",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 9,
    color: "#8E9196",
    marginBottom: 8,
  },

  // Summary section
  summaryBox: {
    backgroundColor: "#F1F0FB",
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderStyle: "solid",
    padding: 8,
    marginBottom: 12,
    borderRadius: 4,
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  summaryLabel: {
    fontSize: 9,
    color: "#7E69AB",
    fontFamily: "Brakle",
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#1A1F2C",
  },

  // Case sections
  caseSection: {
    marginBottom: 12,
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderStyle: "solid",
    borderRadius: 4,
    overflow: "hidden",
  },
  caseHeader: {
    backgroundColor: "#F1F0FB",
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E5DEFF",
    borderBottomStyle: "solid",
  },
  caseName: {
    fontFamily: "Brakle",
    fontWeight: 600,
    fontSize: 11,
    color: "#1A1F2C",
    marginBottom: 2,
  },
  caseStats: {
    fontSize: 8,
    color: "#7E69AB",
  },

  // Items list
  itemsList: {
    padding: 6,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: "#F1F0FB",
    borderBottomStyle: "solid",
    minHeight: 20,
  },
  checkbox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#7E69AB",
    borderStyle: "solid",
    marginRight: 8,
    flexShrink: 0,
  },
  itemContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  itemName: {
    fontSize: 9,
    color: "#221F26",
    fontWeight: "bold",
    flex: 3,
    marginRight: 8,
  },
  itemDetails: {
    fontSize: 8,
    color: "#8E9196",
    flex: 1,
    textAlign: "center",
    marginRight: 8,
  },
  itemCategory: {
    fontSize: 8,
    color: "#7E69AB",
    backgroundColor: "#F1F0FB",
    padding: 2,
    borderRadius: 2,
    flex: 1,
    textAlign: "center",
  },

  // Unassigned section
  unassignedSection: {
    marginTop: 16,
  },
  sectionTitle: {
    fontFamily: "Brakle",
    fontWeight: 600,
    fontSize: 12,
    marginBottom: 8,
    color: "#1A1F2C",
    borderBottomWidth: 1,
    borderBottomColor: "#E5DEFF",
    borderBottomStyle: "solid",
    paddingBottom: 4,
  },

  // Footer
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    textAlign: "center",
    fontSize: 8,
    color: "#8E9196",
    borderTopWidth: 1,
    borderTopColor: "#E5DEFF",
    borderTopStyle: "solid",
    paddingTop: 6,
  },
});

export function PackingListPDF({ items, cases, projectName }: PackingListPDFProps) {
  // Filter out packed items and organize by case
  const unpackedItems = items.filter(item => !item.is_packed);
  
  const assignedCases = cases.map(caseItem => ({
    ...caseItem,
    items: unpackedItems.filter(item => item.assigned_case === caseItem.name),
  })).filter(caseItem => caseItem.items.length > 0); // Only show cases with unpacked items

  const unassignedItems = unpackedItems.filter(item => !item.assigned_case);

  // Calculate totals (unpacked items only)
  const totalItems = unpackedItems.length;
  const totalCases = assignedCases.length;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Top bar */}
        <View style={styles.topBar}>
          <Text style={styles.printedDate}>Generated on {new Date().toLocaleDateString()}</Text>
        </View>

        {/* Header */}
        <View style={styles.headerSection}>
          <View style={styles.leftColumn}>
            <Text style={styles.projectTitle}>{projectName} - Packing List</Text>
          </View>
        </View>

        {/* Cases */}
        {assignedCases.map((caseItem, index) => (
          <View key={caseItem.id} style={styles.caseSection}>
            <View style={styles.caseHeader}>
              <Text style={styles.caseName}>{caseItem.name}</Text>
              <Text style={styles.caseStats}>
                {caseItem.items.length} items to pack
              </Text>
              {caseItem.description && (
                <Text style={styles.caseStats}>{caseItem.description}</Text>
              )}
            </View>
            
            <View style={styles.itemsList}>
              {caseItem.items.map((item: GearItemWithPacking) => (
                <View key={item.id} style={styles.itemRow}>
                  <View style={styles.checkbox} />
                  <View style={styles.itemContent}>
                    <Text style={styles.itemName}>{item.name}</Text>
                    <Text style={styles.itemDetails}>Qty: {item.quantity}</Text>
                    <Text style={styles.itemCategory}>{item.category}</Text>
                  </View>
                </View>
              ))}
              
              {caseItem.items.length === 0 && (
                <Text style={styles.itemDetails}>No items assigned to this case</Text>
              )}
            </View>
          </View>
        ))}

        {/* Unassigned Items */}
        {unassignedItems.length > 0 && (
          <View style={styles.unassignedSection}>
            <Text style={styles.sectionTitle}>Unassigned Items</Text>
            
            <View style={styles.itemsList}>
              {unassignedItems.map((item) => (
                <View key={item.id} style={styles.itemRow}>
                  <View style={styles.checkbox} />
                  <View style={styles.itemContent}>
                    <Text style={styles.itemName}>{item.name}</Text>
                    <Text style={styles.itemDetails}>Qty: {item.quantity}</Text>
                    <Text style={styles.itemCategory}>{item.category}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          Packing List • {projectName} • Page 1
        </Text>
      </Page>
    </Document>
  );
}